// Internal: rendered exclusively by AccountPane based on account type. Not exported from the panes barrel.
import * as React from 'react';

import { CalloutPaneBody } from '../../components/CalloutCarousel';
import TransactionsChart from '../../components/charts/TransactionsChart';
import CreditLimit from '../../components/CreditLimit';
import CurrentBalance from '../../components/CurrentBalance';
import Entity from '../../components/entity';
import AccountPaneHeader from '../../components/headers/AccountPaneHeader';
import { TransactionSummary } from '../../components/summaries';
import TransactionsTable, { TxnTableElementType } from '../../components/tables/TransactionsTable';
import { useFilteredTransactions } from '../../hooks/useFilteredTransactions';
import { useDateRangeParams } from '../../providers/DateRangeProvider';

import type { GetFinancialAccountQuery } from './__generated__/AccountPane.gql';

interface ICreditAccountPaneProps {
  financialAccount: NonNullable<GetFinancialAccountQuery['getFinancialAccount']>;
}

const CreditAccountPane = ({ financialAccount }: ICreditAccountPaneProps) => {
  // Date range synced with URL
  const [dateRange, setDateRange] = useDateRangeParams();

  const { transactions, loading, error } = useFilteredTransactions({
    filters: [{ key: 'financialAccountId', value: financialAccount.id }],
    amountType: 'credit',
  });

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <Entity.Pane>
      <AccountPaneHeader financialAccount={financialAccount} dateRange={dateRange} onDateRangeChange={setDateRange} />
      <CalloutPaneBody
        carouselFilters={{ financialAccountId: financialAccount.id }}
        leftColumn={[
          <React.Fragment key="chart">
            <CurrentBalance currentBalance={financialAccount.currentBalance?.balance} />
            <TransactionsChart
              transactions={transactions}
              dateRange={dateRange}
              isLoading={loading}
              onDateRangeChange={setDateRange}
            />
          </React.Fragment>,
          <TransactionsTable
            key="table"
            financialAccountIds={[financialAccount.id]}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            primaryElement={TxnTableElementType.Merchant}
          />,
        ]}
        rightColumn={
          <>
            {financialAccount.currentBalance?.creditLimit && (
              <CreditLimit
                creditLimit={financialAccount.currentBalance.creditLimit}
                balance={financialAccount.currentBalance.balance}
              />
            )}
            <TransactionSummary transactions={transactions} dateRange={dateRange} />
          </>
        }
      />
    </Entity.Pane>
  );
};

export default CreditAccountPane;
